import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonButton, IonCard, IonCardContent, IonChip, IonNote, IonSegment, IonSegmentButton, IonTextarea } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';

import { AuthService } from '@app/core/services/auth.service';
import { ContextService } from '@app/core/services/context.service';
import { LanguageService } from '@app/core/services/language.service';
import { Reminder, ReminderDateDefinition, ReminderDateMode, ReminderHostType, ReminderOffsetDirection } from '@app/core/models/reminder.model';
import { DynamicIconComponent } from '@app/shared/components/dynamic-icon';

interface ReminderDraft {
  id: string | null;
  note: string;
  dateMode: ReminderDateMode;
  absoluteDate: string;
  offsetDays: number;
  offsetDirection: ReminderOffsetDirection;
}

@Component({
  selector: 'app-claim-document-reminders',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DynamicIconComponent,
    IonButton,
    IonCard,
    IonCardContent,
    IonChip,
    IonNote,
    IonSegment,
    IonSegmentButton,
    IonTextarea,
  ],
  templateUrl: './claim-document-reminders.component.html',
  styleUrls: ['./claim-document-reminders.component.scss'],
})
export class ClaimDocumentRemindersComponent implements OnChanges {
  @Input({ required: true }) claimId = '';
  @Input({ required: true }) documentId = '';
  @Input({ required: true }) baseDate = '';
  @Input() reminders: Reminder[] = [];
  @Input() readonly = false;

  @Output() remindersChange = new EventEmitter<Reminder[]>();

  public visibleReminders: Reminder[] = [];
  public draft: ReminderDraft = this.createEmptyDraft();
  public isCreating = false;
  public editingReminderId: string | null = null;
  public validationMessageKey = '';

  private readonly authService = inject(AuthService);
  private readonly contextService = inject(ContextService);
  private readonly languageService = inject(LanguageService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private localReminders: Reminder[] | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reminders']) {
      this.localReminders = null;
    }

    this.refreshVisibleReminders();
  }

  public startCreate(): void {
    if (this.readonly) {
      return;
    }

    this.isCreating = true;
    this.editingReminderId = null;
    this.validationMessageKey = '';
    this.draft = this.createDraftForCreate();
    this.changeDetectorRef.markForCheck();
  }

  public startEdit(reminder: Reminder): void {
    if (this.readonly) {
      return;
    }

    this.isCreating = false;
    this.editingReminderId = reminder.id;
    this.validationMessageKey = '';
    const storedDateDefinition = this.getReminderDateDefinition(reminder);
    const dateMode = storedDateDefinition?.mode || 'absolute';
    this.draft = {
      id: reminder.id,
      note: this.getReminderNote(reminder),
      dateMode,
      absoluteDate: this.toDateInputValue(this.getReminderDate(reminder)),
      offsetDays: this.getStoredOffsetDays(reminder, storedDateDefinition),
      offsetDirection: this.getStoredOffsetDirection(reminder, storedDateDefinition),
    };
    this.changeDetectorRef.markForCheck();
  }

  public cancelEditing(): void {
    this.isCreating = false;
    this.editingReminderId = null;
    this.validationMessageKey = '';
    this.draft = this.createEmptyDraft();
    this.changeDetectorRef.markForCheck();
  }

  public saveDraft(): void {
    const validationError = this.validateDraft();
    if (validationError) {
      this.validationMessageKey = validationError;
      this.changeDetectorRef.markForCheck();
      return;
    }

    const resolvedReminderDate = this.resolveReminderDate();
    const dateDefinition = this.buildDateDefinition();
    const now = new Date();
    const currentReminders = this.getActiveReminderSource();

    let nextReminders: Reminder[];

    if (this.editingReminderId) {
      nextReminders = currentReminders.map((reminder) => {
        if (reminder.id !== this.editingReminderId) {
          return reminder;
        }

        return {
          ...reminder,
          hostType: ReminderHostType.CLAIMS,
          hostId: this.claimId,
          isActive: true,
          isDeleted: false,
          updatedAt: now,
          jsonPayload: {
            ...reminder.jsonPayload,
            documentId: this.documentId,
            note: this.draft.note.trim(),
            reminderDate: resolvedReminderDate,
            dateDefinition,
          },
        };
      });
    } else {
      const newReminder: Reminder = {
        id: this.generateId(),
        clientId: this.contextService.getCurrentClientId() || '',
        subsidiaryId: this.contextService.getCurrentSubsidiaryId() || null,
        userId: this.authService.currentUserValue?.id || '',
        hostType: ReminderHostType.CLAIMS,
        hostId: this.claimId,
        isActive: true,
        isDeleted: false,
        createdAt: now,
        updatedAt: now,
        jsonPayload: {
          documentId: this.documentId,
          note: this.draft.note.trim(),
          reminderDate: resolvedReminderDate,
          dateDefinition,
        },
      };

      nextReminders = [...currentReminders, newReminder];
    }

    this.publishReminderChanges(nextReminders);
    this.cancelEditing();
  }

  public deleteReminder(reminder: Reminder): void {
    if (this.readonly) {
      return;
    }

    const now = new Date();
    const nextReminders = this.getActiveReminderSource().map((item) => {
      if (item.id !== reminder.id) {
        return item;
      }

      return {
        ...item,
        isActive: false,
        isDeleted: true,
        updatedAt: now,
      };
    });

    this.publishReminderChanges(nextReminders);

    if (this.editingReminderId === reminder.id) {
      this.cancelEditing();
    }
  }

  public trackByReminderId(index: number, reminder: Reminder): string {
    return reminder.id;
  }

  public isEditing(reminder: Reminder): boolean {
    return this.editingReminderId === reminder.id;
  }

  public formatReminderDate(reminderDate: string): string {
    return this.formatLocalizedDate(reminderDate);
  }

  public get displayBaseDate(): string {
    return this.formatLocalizedDate(this.baseDate);
  }

  private formatLocalizedDate(value: string): string {
    const parsedDate = this.parseStoredDate(value);
    return new Intl.DateTimeFormat(this.languageService.currentLanguage || undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(parsedDate);
  }

  public getReminderTone(reminder: Reminder): 'danger' | 'warning' | 'primary' {
    const reminderDate = this.parseStoredDate(this.getReminderDate(reminder));
    const today = this.parseYmd(this.getTodayYmd());
    const reminderDay = this.toDateInputValue(reminderDate.toISOString());
    const todayDay = this.toDateInputValue(today.toISOString());

    if (reminderDay < todayDay) {
      return 'danger';
    }

    if (reminderDay === todayDay) {
      return 'warning';
    }

    return 'primary';
  }

  public getReminderNote(reminder: Reminder): string {
    return reminder.jsonPayload.note || '';
  }

  public getReminderDate(reminder: Reminder): string {
    return reminder.jsonPayload.reminderDate || '';
  }

  private buildDateDefinition(): ReminderDateDefinition {
    if (this.draft.dateMode === 'absolute') {
      return { mode: 'absolute' };
    }

    return {
      mode: 'relative',
      offsetDays: Number(this.draft.offsetDays),
      offsetDirection: this.draft.offsetDirection,
    };
  }

  private publishReminderChanges(nextReminders: Reminder[]): void {
    this.localReminders = nextReminders;
    this.refreshVisibleReminders();
    this.remindersChange.emit(nextReminders);
  }

  private refreshVisibleReminders(): void {
    this.visibleReminders = this.getActiveReminderSource()
      .filter((reminder) => this.isReminderForCurrentDocument(reminder))
      .filter((reminder) => !reminder.isDeleted)
      .sort((left, right) => this.getReminderDate(left).localeCompare(this.getReminderDate(right)));

    this.changeDetectorRef.markForCheck();
  }

  private getActiveReminderSource(): Reminder[] {
    return this.localReminders ?? this.reminders;
  }

  private isReminderForCurrentDocument(reminder: Reminder): boolean {
    return (
      reminder.hostType === ReminderHostType.CLAIMS &&
      reminder.hostId === this.claimId &&
      reminder.jsonPayload.documentId === this.documentId
    );
  }

  private createEmptyDraft(): ReminderDraft {
    return {
      id: null,
      note: '',
      dateMode: 'absolute',
      absoluteDate: '',
      offsetDays: 0,
      offsetDirection: 'after',
    };
  }

  private createDraftForCreate(): ReminderDraft {
    return {
      id: null,
      note: '',
      dateMode: 'relative',
      absoluteDate: this.baseDate,
      offsetDays: 0,
      offsetDirection: 'after',
    };
  }

  private validateDraft(): string {
    if (!this.draft.note.trim()) {
      return 'CLAIM_DOCUMENT_REMINDERS.VALIDATION_NOTE_REQUIRED';
    }

    if (this.draft.dateMode === 'absolute') {
      if (!this.isValidYmd(this.draft.absoluteDate)) {
        return 'CLAIM_DOCUMENT_REMINDERS.VALIDATION_DATE_REQUIRED';
      }
      return '';
    }

    if (!this.parseDateValue(this.baseDate)) {
      return 'CLAIM_DOCUMENT_REMINDERS.VALIDATION_BASE_DATE_INVALID';
    }

    if (!Number.isInteger(Number(this.draft.offsetDays)) || Number(this.draft.offsetDays) < 0) {
      return 'CLAIM_DOCUMENT_REMINDERS.VALIDATION_OFFSET_INVALID';
    }

    return '';
  }

  private resolveReminderDate(): string {
    if (this.draft.dateMode === 'absolute') {
      return this.parseYmd(this.draft.absoluteDate).toISOString();
    }

    const baseDate = this.parseDateValue(this.baseDate);
    if (!baseDate) {
      throw new Error('Invalid reminder base date');
    }
    const directionMultiplier = this.draft.offsetDirection === 'before' ? -1 : 1;
    baseDate.setUTCDate(baseDate.getUTCDate() + (Number(this.draft.offsetDays) * directionMultiplier));
    return baseDate.toISOString();
  }

  private calculateOffsetDays(reminderDate: string): number {
    const base = this.parseDateValue(this.baseDate);
    const target = this.parseStoredDate(reminderDate);
    if (!base) {
      return 0;
    }
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.abs(Math.round((target.getTime() - base.getTime()) / millisecondsPerDay));
  }

  private calculateOffsetDirection(reminderDate: string): ReminderOffsetDirection {
    const base = this.parseDateValue(this.baseDate);
    const target = this.parseStoredDate(reminderDate);
    if (!base) {
      return 'after';
    }
    return target.getTime() < base.getTime() ? 'before' : 'after';
  }

  private getReminderDateDefinition(reminder: Reminder): ReminderDateDefinition | null {
    const candidate = reminder.jsonPayload.dateDefinition;
    if (!candidate || typeof candidate !== 'object') {
      return null;
    }

    const mode = candidate.mode;
    if (mode !== 'absolute' && mode !== 'relative') {
      return null;
    }

    const offsetDirection = candidate.offsetDirection === 'before' ? 'before' : 'after';
    const offsetDays = typeof candidate.offsetDays === 'number' ? Math.max(0, Math.round(candidate.offsetDays)) : undefined;

    return {
      mode,
      offsetDays,
      offsetDirection,
    };
  }

  private getStoredOffsetDays(reminder: Reminder, dateDefinition: ReminderDateDefinition | null): number {
    if (dateDefinition?.mode === 'relative' && typeof dateDefinition.offsetDays === 'number') {
      return dateDefinition.offsetDays;
    }

    return this.calculateOffsetDays(this.getReminderDate(reminder));
  }

  private getStoredOffsetDirection(reminder: Reminder, dateDefinition: ReminderDateDefinition | null): ReminderOffsetDirection {
    if (dateDefinition?.mode === 'relative' && dateDefinition.offsetDirection) {
      return dateDefinition.offsetDirection;
    }

    return this.calculateOffsetDirection(this.getReminderDate(reminder));
  }

  private parseStoredDate(value: string): Date {
    return this.parseDateValue(value) || this.parseDateValue(this.baseDate) || this.getTodayAtUtcNoon();
  }

  private parseYmd(value: string): Date {
    const [year, month, day] = value.split('-').map((part) => Number(part));
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  }

  private parseDateValue(value: string): Date | null {
    if (!value || !value.trim()) {
      return null;
    }

    if (this.isValidYmd(value)) {
      return this.parseYmd(value);
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(), 12, 0, 0));
  }

  private isValidYmd(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
  }

  private toDateInputValue(value: string): string {
    if (this.isValidYmd(value)) {
      return value;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    return parsed.toISOString().slice(0, 10);
  }

  private getTodayYmd(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private getTodayAtUtcNoon(): Date {
    return this.parseYmd(this.getTodayYmd());
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `rem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}